import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Injector, computed, isDevMode, signal } from '@angular/core';

import { SupabaseService } from '../services/supabase.service';
import {
  DEFAULT_LOCALE,
  LOCALE_TAGS,
  AppLocale,
  isAppLocale
} from './i18n.types';
import {
  FR_TRANSLATIONS,
  TRANSLATIONS,
  TranslationKey,
  TranslationParams
} from './translations';

const LOCAL_STORAGE_KEY = 'lifeops.locale';

@Injectable({ providedIn: 'root' })
export class I18nService {
  private readonly localeState = signal<AppLocale>(this.detectDeviceLocale());

  readonly locale = this.localeState.asReadonly();
  readonly localeTag = computed(() => LOCALE_TAGS[this.locale()]);

  constructor(
    private readonly injector: Injector,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.applyDocumentLocale(this.localeState());
  }

  private get supabase(): SupabaseService {
    return this.injector.get(SupabaseService);
  }

  async initialize(): Promise<void> {
    this.applyLocale(this.detectDeviceLocale(), false);
    const session = await this.supabase.ensureSession();
    if (session) {
      await this.syncWithCurrentUser();
    }
  }

  async syncWithCurrentUser(): Promise<void> {
    const userId = this.supabase.userId;
    if (!userId) return;

    const locale = await this.supabase.getProfileLocale();
    if (!isAppLocale(locale)) return;

    this.applyLocale(locale, true);
  }

  async setLocale(locale: AppLocale, persistToProfile = true): Promise<void> {
    const previous = this.localeState();
    this.applyLocale(locale, true);

    const userId = this.supabase.userId;
    if (!persistToProfile || !userId) return;

    try {
      await this.supabase.updateProfileLocale(locale);
    } catch (error) {
      this.applyLocale(previous, true);
      throw error;
    }
  }

  t(key: TranslationKey, params: TranslationParams = {}): string {
    const selected = TRANSLATIONS[this.locale()][key];
    const fallback = FR_TRANSLATIONS[key];
    const template = selected ?? fallback ?? (isDevMode() ? key : '');

    return template.replace(/\{([a-zA-Z0-9_]+)\}/g, (match, name: string) => {
      const value = params[name];
      return value === undefined || value === null ? match : String(value);
    });
  }

  formatDate(
    value: string | number | Date,
    options: Intl.DateTimeFormatOptions
  ): string {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat(this.localeTag(), options).format(date);
  }

  formatDateOnly(
    value: string,
    options: Intl.DateTimeFormatOptions
  ): string {
    return this.formatDate(new Date(`${value}T12:00:00`), options);
  }

  formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
    return new Intl.NumberFormat(this.localeTag(), options).format(value);
  }

  private applyLocale(locale: AppLocale, persistLocally: boolean): void {
    this.localeState.set(locale);
    this.applyDocumentLocale(locale);

    if (persistLocally && typeof localStorage !== 'undefined') {
      localStorage.setItem(LOCAL_STORAGE_KEY, locale);
    }
  }

  private applyDocumentLocale(locale: AppLocale): void {
    this.document.documentElement.lang = locale;
  }

  private detectDeviceLocale(): AppLocale {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (isAppLocale(stored)) return stored;
    }

    if (typeof navigator !== 'undefined') {
      const browserLocale = navigator.language?.toLowerCase().split('-')[0];
      if (isAppLocale(browserLocale)) return browserLocale;
    }

    return DEFAULT_LOCALE;
  }
}
